import React from "react";
import { Avatar, Box, Button, Chip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import moment from "moment";
import { convertTime } from "src/utils/time";

const DATE_FORMAT = "MMM DD, YYYY hh:mm A";

// The same frame as ArtifactCard: a thin border that turns blue on hover.
const cardSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  bgcolor: "background.paper",
  px: 2,
  py: 1.5,
  mb: 1,
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    md: "minmax(200px, 1.3fr) minmax(200px, 1.4fr) minmax(190px, 1fr) minmax(190px, 1fr) 120px",
  },
  columnGap: 2.5,
  rowGap: 1.5,
  alignItems: "center",
  transition: "border-color 0.15s, box-shadow 0.15s",
  "&:hover": { borderColor: "#3fa7d6", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)" },
};

const columnLabelSx = { display: "block", color: "text.secondary", fontSize: "0.7rem", fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", mb: 0.25 };

// A time in the viewer's zone when the record carries its own zone, otherwise as
// stored. "—" when there is no time yet (a check-out while still checked in).
function formatTime(value, timezone, utcFallback) {
  if (!value) return "—";
  return convertTime(value, timezone, moment.tz.guess(), DATE_FORMAT)
    ?? (utcFallback ? moment(value).utc() : moment(value)).format(DATE_FORMAT);
}

function IconLine({ icon, children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, color: "text.secondary", fontSize: "0.85rem", "& svg": { fontSize: "1rem", flexShrink: 0 } }}>
      {icon}
      <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{children || "—"}</Box>
    </Box>
  );
}

function TimeRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 30, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>{value}</Typography>
    </Box>
  );
}

// One employee's current task and last check-in/out, on the Locate screen.
const EmployeeLocateCard = (props) => {
  const { data } = props;
  const name = `${data.emp_first_name || ""} ${data.emp_last_name || ""}`.trim();
  const initials = `${(data.emp_first_name || "").charAt(0)}${(data.emp_last_name || "").charAt(0)}`.toUpperCase();

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <Avatar sx={{ width: 40, height: 40, fontSize: "0.9rem", fontWeight: 600, bgcolor: alpha("#3fa7d6", 0.12), color: "#3fa7d6" }}>
          {initials || "?"}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {name || "—"}
          </Typography>
          <IconLine icon={<AssignmentOutlinedIcon />}>{data.tas_title}</IconLine>
          <IconLine icon={<FolderOutlinedIcon />}>{data.pro_title}</IconLine>
        </Box>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography component="span" sx={columnLabelSx}>Location</Typography>
        <Box sx={{ display: "flex", gap: 0.75, "& svg": { fontSize: "1.05rem", color: "text.secondary", mt: "2px", flexShrink: 0 } }}>
          <PlaceOutlinedIcon />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">{data.empAddress || "—"}</Typography>
            {data.UpdatedTime && (
              <Typography variant="caption" color="text.secondary">
                Updated {moment(data.UpdatedTime.replace(/Z/g, "")).fromNow()}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box>
        <Typography component="span" sx={columnLabelSx}>Scheduled</Typography>
        <TimeRow label="Start" value={formatTime(data.tas_start_time, data.task_emp_timezone, false)} />
        <TimeRow label="End" value={formatTime(data.tas_end_time, data.task_emp_timezone, false)} />
      </Box>

      <Box>
        <Typography component="span" sx={columnLabelSx}>Last Activity</Typography>
        <TimeRow label="In" value={formatTime(data.empLocdate, data.empLocate_timezone, true)} />
        <TimeRow label="Out" value={formatTime(data.checkoutEmpDate, data.empLocate_timezone, true)} />
      </Box>

      <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, alignItems: { xs: "center", md: "flex-end" }, gap: 1 }}>
        {data.checkin ? (
          <Chip label="Checked In" color="success" size="small" />
        ) : (
          <Chip label="Checked Out" size="small" variant="outlined" />
        )}
        {data.checkin && (
          <Button size="small" variant="outlined" onClick={() => props.toggleShowCheckOutDialog(data)}>
            Check Out
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default EmployeeLocateCard;
